// src/components/assistant/AssistantPanel.js
// "Let an AI assistant help fill this in" (MCP flow B): assistant code + per-assistant setup tabs.
import React, { useState } from "react";
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, InputAdornment,
  Link, Stack, Tab, Tabs, TextField, Tooltip, Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import KeyIcon from "@mui/icons-material/VpnKeyOutlined";
import BlockIcon from "@mui/icons-material/Block";
import { createAssistantCode, revokeAssistantCodes } from "../../api";
import { ASSISTANT_GUIDES_CHECKED, ASSISTANT_TABS, EXAMPLE_PROMPTS, IN_EVERY_PATH, MCP_SERVER_URL } from "../../content/assistants";
import { copyText } from "../../utils/clipboard";
import { friendlyError } from "../../utils/errors";

function CopyField({ label, value, multiline }) {
  const [copied, setCopied] = useState(false);
  return (
    <TextField
      label={label}
      value={value}
      fullWidth
      size="small"
      multiline={multiline}
      InputProps={{
        readOnly: true,
        endAdornment: (
          <InputAdornment position="end">
            <Tooltip title={copied ? "Copied" : "Copy"}>
              <IconButton aria-label={`Copy ${label}`} edge="end" onClick={async () => setCopied(await copyText(value))}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </InputAdornment>
        ),
      }}
      onFocus={(e) => e.target.select()}
    />
  );
}

export default function AssistantPanel({ open, onClose, onCodesChanged, onOpenChatbot }) {
  const [tab, setTab] = useState(0);
  const [code, setCode] = useState(null);
  const [expires, setExpires] = useState(null);
  const [message, setMessage] = useState(null);
  const [busy, setBusy] = useState(false);
  const t = ASSISTANT_TABS[tab];

  const generate = async () => {
    setBusy(true);
    setMessage(null);
    try {
      const res = await createAssistantCode();
      setCode(res.assistant_code);
      setExpires(new Date(res.expires_at));
      onCodesChanged?.();
    } catch (err) {
      setMessage({ type: "error", text: friendlyError(err) });
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    setBusy(true);
    try {
      const res = await revokeAssistantCodes();
      setCode(null);
      setMessage({ type: "success", text: res.revoked ? "Assistant access removed. Old codes no longer work." : "There were no active codes." });
      onCodesChanged?.();
    } catch (err) {
      setMessage({ type: "error", text: friendlyError(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth aria-labelledby="assistant-panel-title">
      <DialogTitle id="assistant-panel-title" sx={{ fontWeight: 700 }}>Let an AI assistant help fill this in</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Your own AI assistant can ask you questions and pre-fill steps 1–3 here. For step 4 it sends a summary of
          what the learner already knows, and the tool marks the matching MLUs itself. It can only make
          <strong> proposals</strong>: you review and confirm every step in this tool, and the AI steps (5 and 7) run only here.
        </Typography>

        <Box sx={{ p: 2, borderRadius: 2, bgcolor: "grey.50", border: "1px solid", borderColor: "divider", mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>1. Connect the T4R server in your assistant</Typography>
          <CopyField label="MCP server URL" value={MCP_SERVER_URL} />
          <Typography variant="subtitle2" sx={{ mt: 2 }} gutterBottom>2. Give your assistant this code</Typography>
          {code ? (
            <Stack spacing={1.5}>
              <CopyField label="Assistant code" value={code} />
              <CopyField label="Message to paste into your assistant" value={EXAMPLE_PROMPTS.withCode(code)} multiline />
              <Typography variant="caption" color="text.secondary">
                Valid until {expires?.toLocaleString()}. It only allows proposals for steps 1–4 of this journey.
              </Typography>
            </Stack>
          ) : (
            <Button startIcon={<KeyIcon />} variant="contained" onClick={generate} disabled={busy}>Create assistant code</Button>
          )}
          <Box sx={{ mt: 1.5 }}>
            <Button size="small" color="error" startIcon={<BlockIcon />} onClick={revoke} disabled={busy}>
              Remove assistant access (revoke all codes)
            </Button>
          </Box>
          {message && <Alert severity={message.type} sx={{ mt: 1.5 }} role="status">{message.text}</Alert>}
        </Box>

        <Typography variant="subtitle2" gutterBottom>How to add the connector</Typography>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" allowScrollButtonsMobile aria-label="Assistants">
          {ASSISTANT_TABS.map((a) => (
            <Tab key={a.id} label={
              <Stack direction="row" spacing={0.75} alignItems="center">
                <span>{a.name}</span>
                {a.recommended && <Chip size="small" color="primary" label={a.badge || "Recommended"} />}
              </Stack>
            } />
          ))}
        </Tabs>
        <Box role="tabpanel" sx={{ pt: 2 }}>
          {t.subtitle && <Typography variant="caption" color="text.secondary">{t.subtitle}</Typography>}
          <Box component="ol" sx={{ pl: 2.5, mt: 1 }}>
            {t.steps.map((s) => <Typography component="li" variant="body2" key={s} sx={{ mb: 0.5 }}>{s}</Typography>)}
          </Box>
          {t.planNote && <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{t.planNote}</Typography>}
          {t.helpUrl && <Link href={t.helpUrl} target="_blank" rel="noopener" variant="body2">{t.helpLabel || "Official help page"}</Link>}
          {t.id === "none" && onOpenChatbot && (
            <Button size="small" variant="outlined" onClick={onOpenChatbot} sx={{ mt: 1 }}>Prepare with any AI chatbot</Button>
          )}
        </Box>

        <Alert severity="info" sx={{ mt: 2 }} icon={false}>
          {IN_EVERY_PATH.map((r) => <div key={r}>• {r}</div>)}
        </Alert>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
          Setup steps checked against the vendors' help pages on {ASSISTANT_GUIDES_CHECKED}; menus can change.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
